import gql from 'graphql-tag';

import scheduleFieldsFragment from '../../schedule/fragments/schedule-fields-fragment.gql';
import baseDispensaryFragment from '../fragments/base-dispensary.gql';
import taxProperties from '../fragments/tax-properties-fragment.gql';
import weeklyHoursFragment from '../fragments/weekly-hours-fragment.gql';
import hoursSettingsForOrderTypeFragment from '../fragments/hours-settings-for-order-type-fragment.gql';
import inStorePickupOrderingSettingsFragment from '../fragments/in-store-pickup-ordering-settings-fragment.gql';
import curbsidePickupOrderingSettingsFragment from '../fragments/curbside-pickup-ordering-settings-fragment.gql';
import driveThruPickupOrderingSettingsFragment from '../fragments/drive-thru-pickup-ordering-settings-fragment.gql';
import deliveryOrderingSettingsFragment from '../fragments/delivery-ordering-settings-fragment.gql';
import deliveryZonesFragment from '../fragments/delivery-zones-fragment.gql';
import kioskOrderingSettingsFragment from '../fragments/kiosk-ordering-settings-fragment.gql';
import menuSectionFragment from '../fragments/menu-section-fragment.gql';

export const chainDispensaryIds = gql`
  query ChainDispensaryIds($chainId: String, $status: String) {
    chainDispensaryIds(chainId: $chainId, status: $status)
  }
`;

export const chainDispensariesByStatus = gql`
  query ChainDispensariesByStatus($chainId: String, $status: String) {
    chainDispensariesByStatus(chainId: $chainId, status: $status) {
      id
      retailer {
        id
        assignedToBillingGroup
      }
    }
  }
`;

export const dispensaryAvailableReservations = gql`
  query DispensaryAvailableReservations($dispensaryFilter: dispensariesFilterInput!) {
    filteredDispensaries(filter: $dispensaryFilter) {
      reservationsAvailableByOrderType {
        inStorePickup {
          slotId
          startTimeISO
          endTimeISO
          quantityRemaining
          isNextDaySlot
          dayOfWeek
        }
        curbsidePickup {
          slotId
          startTimeISO
          endTimeISO
          quantityRemaining
          isNextDaySlot
          dayOfWeek
        }
        driveThruPickup {
          slotId
          startTimeISO
          endTimeISO
          quantityRemaining
          isNextDaySlot
          dayOfWeek
        }
        delivery {
          slotId
          deliveryAreaId
          active
          startTimeISO
          endTimeISO
          quantityRemaining
          isNextDaySlot
          dayOfWeek
        }
        pickup {
          slotId
          startTimeISO
          endTimeISO
          quantityRemaining
          isNextDaySlot
          dayOfWeek
        }
      }
    }
  }
`;

export const consumerDispensaries = gql`
  ${baseDispensaryFragment}
  ${taxProperties}
  ${weeklyHoursFragment}
  ${hoursSettingsForOrderTypeFragment}
  ${inStorePickupOrderingSettingsFragment}
  ${curbsidePickupOrderingSettingsFragment}
  ${driveThruPickupOrderingSettingsFragment}
  ${deliveryOrderingSettingsFragment}
  ${kioskOrderingSettingsFragment}
  ${scheduleFieldsFragment}
  ${menuSectionFragment}
  query ConsumerDispensaries($dispensaryFilter: dispensariesFilterInput!) {
    filteredDispensaries(filter: $dispensaryFilter) {
      ...baseDispensaryFragment
      hasMenuIntegration
      activeCategories
      consumerDispensaryIntegrations {
        alpineiq {
          enabled
          flags {
            combineDiscounts
          }
          credentials {
            rewardsProgramPointsBoost
          }
        }
        springbig {
          enabled
          flags {
            combineDiscounts
          }
        }
        dutchieposloyalty {
          enabled
          flags {
            combineDiscounts
          }
        }
        leaflogix {
          enabled
          integrationType
        }
      }
      googleAnalyticsID
      googleTagManager {
        gtmID
        flags
        containerIds
        serverContainerUrl
        serverContainerUrls
      }
      hoursSettings {
        inStorePickup {
          ...hoursSettingsForOrderTypeFragment
          effectiveHours {
            ...weeklyHoursFragment
          }
        }
        curbsidePickup {
          ...hoursSettingsForOrderTypeFragment
          effectiveHours {
            ...weeklyHoursFragment
          }
        }
        driveThruPickup {
          ...hoursSettingsForOrderTypeFragment
          effectiveHours {
            ...weeklyHoursFragment
          }
        }
        delivery {
          ...hoursSettingsForOrderTypeFragment
          effectiveHours {
            ...weeklyHoursFragment
          }
        }
      }
      orderTypesConfigV2 {
        inStorePickup {
          ...inStorePickupOrderingSettingsFragment
        }
        curbsidePickup {
          ...curbsidePickupOrderingSettingsFragment
        }
        driveThruPickup {
          ...driveThruPickupOrderingSettingsFragment
        }
        delivery {
          ...deliveryOrderingSettingsFragment
        }
        kiosk {
          ...kioskOrderingSettingsFragment
        }
        offerAnyPickupService
        offerDeliveryService
      }
      retailer {
        enterpriseId
      }
      enabledOrderTypes {
        curbsidePickup
        delivery
        driveThruPickup
        inStorePickup
        kiosk
        pickup
      }
      merrcoPublicToken
      messagingSettings {
        disableReadyForPickup
        disableStartDelivery
        disablePushReadyForPickup
        disablePushStartDelivery
      }
      remoteExtensions {
        theme {
          manifestUrl
          modulePath
          scope
        }
      }
      taxConfig {
        calculationMethod
        discountTaxOrder
        taxes {
          ...taxProperties
          destinationRate
        }
        version
      }
      webCustomizationSettings {
        colorSettings {
          navBarColor
          linkColor
        }
        fontSettings {
          family
        }
      }
      webCustomizationSettingsV2 {
        colors {
          buttonsLinks
          navBar
          staffPickTag
          discountTag
          productCardButton
          strainTypes {
            cbd
            cbg
            default
            highCBD
            hybrid
            indica
            indicaHybrid
            sativa
            sativaHybrid
            seed
            thc
          }
        }
        fonts {
          body
          heading
        }
        textTransforms {
          buttons
          links
          tags
        }
        radius {
          default
        }
        categories {
          type
          style
          mobileDisplay
          items {
            category
            image
            collectionId
          }
        }
        productCards {
          defaultViewLarge
          defaultViewSmall
          fullWidthImages
          showProductWeight
          textAlignment
          disableProductCardImageCycling
        }
        concierge {
          accentColorHex
          canvasColorHex
        }
      }
      imageBanners(activeOnly: true) {
        _id
        image
        mobileImage
        link
        openInNewTab
        alt
        bannerType
        connectedId
        connectedType
        platforms {
          enabled
          link
          openInNewTab
          type
        }
        position
        schedule {
          ...ScheduleFields
        }
      }
      menuSections {
        ...menuSectionFragment
      }
      collectionsNavigation(customCategory: true, enabled: true) {
        id
        slug
        title
        customCategory
        enabled
        navigationTitle
        isActive
        schedule {
          id
          startDate
          endDate
          timeRanges {
            id
            dayOfWeek
            startTime
            endTime
          }
        }
      }
      adSettings {
        enableAdsForDefault
        enableAdsForEmbedded
        enableAdsForKiosk
        enableAdsForStoreFront
      }
      personalizationSettings {
        enablePersonalizationForDefault
        enablePersonalizationForEmbedded
        enablePersonalizationForKiosk
        enablePersonalizationForStoreFront
        enableSortByRelevance
      }
      tags {
        productTags {
          tagId
          tagName
        }
        inventoryTags {
          tagId
          tagName
        }
      }
    }
  }
`;

export const adminDispensaries = gql`
  ${baseDispensaryFragment}
  ${taxProperties}
  ${hoursSettingsForOrderTypeFragment}
  ${inStorePickupOrderingSettingsFragment}
  ${curbsidePickupOrderingSettingsFragment}
  ${driveThruPickupOrderingSettingsFragment}
  ${deliveryOrderingSettingsFragment}
  ${deliveryZonesFragment}
  ${kioskOrderingSettingsFragment}
  query AdminDispensaries($dispensaryFilter: dispensariesFilterInput!) {
    filteredDispensaries(filter: $dispensaryFilter) {
      ...baseDispensaryFragment
      updatedBy
      adSettings {
        enableAdsForDefault
        enableAdsForEmbedded
        enableAdsForKiosk
        enableAdsForStoreFront
      }
      specialHours {
        name
        startDate
        endDate
        hoursPerDay {
          date
          pickupHours {
            active
            start
            end
          }
          deliveryHours {
            active
            start
            end
          }
        }
      }
      hoursSettings {
        inStorePickup {
          ...hoursSettingsForOrderTypeFragment
        }
        curbsidePickup {
          ...hoursSettingsForOrderTypeFragment
        }
        driveThruPickup {
          ...hoursSettingsForOrderTypeFragment
        }
        delivery {
          ...hoursSettingsForOrderTypeFragment
        }
      }
      orderTypesConfigV2 {
        inStorePickup {
          ...inStorePickupOrderingSettingsFragment
        }
        curbsidePickup {
          ...curbsidePickupOrderingSettingsFragment
        }
        driveThruPickup {
          ...driveThruPickupOrderingSettingsFragment
        }
        delivery {
          ...deliveryOrderingSettingsFragment
          ...deliveryZonesFragment
        }
        kiosk {
          ...kioskOrderingSettingsFragment
          # Admin-only kiosk PIN/claim-state fields, inlined here since this query is admin-only.
          kiosks {
            code
            claimedAt
            lastSeenAt
          }
        }
        offerAnyPickupService
        offerDeliveryService
      }
      enabledOrderTypes {
        curbsidePickup
        delivery
        driveThruPickup
        inStorePickup
        kiosk
        pickup
      }
      blockedAddresses {
        id
        address
        note
        createdAt
      }
      tier
      timezone
      embeddedMenuUrl
      menuIntegration {
        live
        adapter
        lastSyncAt
        lastSyncAtISO
        integrationCategory
      }
      fleetManagementIntegration {
        live
        adapter
        flags {
          fleetManagementStatus
          onfleetStatus
        }
      }
      menuWeights
      menuOrderSettings {
        category
        menuOrder
      }
      featuredBrandsSettings {
        category
        brandIds
      }
      featuredProductsSettings {
        category
        productIds
      }
      isLibrary
      hideFromCCT
      firstActiveAt
      firstActiveAtISO
      embedSettings {
        iframeCSS
        pageCSS
        autoGTM
        autoScroll
        autoScrollOffset
        disclaimerTextHtml
        disableRouting
        disablePageLoadsAtTop
        enableFullScreenEmbeddedCheckout
        applyToAllLocations
      }
      ordersConfig {
        posItemNames
        autoConfirm
        autoClose
        terminalReceiptOptions {
          customerNameOption
          birthdate
          deliveryAddress
          phone
          medical
          potencyInfo
          disableSocialImpact
          enableSurveyQrCode
          sortLineItemsBy
          groupLineItemsBy
        }
      }
      menuScore
      menuScoresByCategory {
        category
        value
      }
      productCategorySummary {
        category
        value
      }
      customDomainSettings {
        domain
      }
      plusSettings {
        checkoutUrl
        defaultReturnUrl
      }
      printedMenuSettings {
        Categories {
          category
          enabled
        }
        StrainTypes {
          strainType
          backgroundColor
          borderColor
        }
        Photos {
          enabled
        }
        HeaderText {
          text
        }
        FooterText {
          text
        }
        PageBreaks {
          enabled
        }
        MenuType {
          type
        }
      }
      retailer {
        id
        assignedToBillingGroup
        enterpriseId
        enterprise {
          id
          billingVersion
        }
      }
      taxConfig {
        version
        calculationMethod
        discountTaxOrder
        taxes {
          ...taxProperties
        }
      }
      messagingSettings {
        disableReadyForPickup
        disableStartDelivery
        disablePushReadyForPickup
        disablePushStartDelivery
      }
      orderStatusEmailSettings {
        disableSubmitted
        disableConfirmed
        disableReadyForPickup
        disableOutForDelivery
        disablePaymentLifecycle
        disablePaymentError
      }
      storeSettings {
        rewardsIntegrationConfiguration {
          rewardsProgramDisplayDescription
          rewardsProgramDisplayEnrollment
          rewardsProgramDisplayName
        }
        priceRangeFilter {
          enabled
          bands {
            bandId
            label
            min
            max
          }
        }
      }
      webCustomizationSettings {
        colorSettings {
          navBarColor
          linkColor
        }
        fontSettings {
          family
        }
      }
      personalizationSettings {
        enablePersonalizationForDefault
        enablePersonalizationForEmbedded
        enablePersonalizationForKiosk
        enablePersonalizationForStoreFront
        enableSortByRelevance
      }
      tags {
        productTags {
          tagId
          tagName
        }
        inventoryTags {
          tagId
          tagName
        }
      }
    }
  }
`;

export const chainDispensaryNamesAndIds = gql`
  query ChainDispensaryNamesAndIds($chainId: String) {
    chainDispensaryNamesAndIds(chainId: $chainId) {
      id
      name
      address
      location {
        state
        city
      }
    }
  }
`;

export const getDeliveryInfo = gql`
  query GetDeliveryInfo($input: getDeliveryInfoInput!) {
    getDeliveryInfo(input: $input) {
      deliveryAreaId
      withinBounds
      fee
      minimum
      feeVaries
      minimumVaries
    }
  }
`;

export const activeDispensaries = gql`
  query ActiveDispensaries($params: activeDispensariesInput) {
    activeDispensaries(params: $params) {
      meta {
        allStates
      }
      dispensaries {
        _id
        integration {
          adapter
          lastSyncAt
        }
        profile {
          cName
          connectedProductPercentage
          embeddedPageViews
          hideFromCCT
          lastThirtyDaysSales
          menuScore
          name
          status
        }
      }
    }
  }
`;

export const getTimezone = gql`
  query GetTimezone($lat: Float, $lng: Float, $timeStamp: Int!) {
    getTimezone(lat: $lat, lng: $lng, timeStamp: $timeStamp) {
      timezone
    }
  }
`;
